import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IdAdMediumDto } from '@/services/ws-consulting/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useMarkedOptionLabel } from 'src/hooks/useMarkedOptionLabel';
import type { Option } from 'src/types';
import { useConsultingAdMediumOptions } from '../hooks/useConsultingAdMediumOptions';

interface ConsultingAdMediumFilterProps {
  value: Option<IdAdMediumDto> | null;
  onChange: (value: SingleValue<Option<IdAdMediumDto>>) => void;
  label?: string;
}

export const ConsultingAdMediumFilter = React.memo(function ConsultingAdMediumFilter({
  value,
  onChange,
  label,
}: ConsultingAdMediumFilterProps) {
  const [t] = useTranslation(['common']);
  const { options, pending: loading, total, searchFilter, setSearchFilter } = useConsultingAdMediumOptions();
  const markedOptionLabel = useMarkedOptionLabel({ t });

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IdAdMediumDto>>) => {
    onChange(newValue);
  });

  return (
    <Filter<IdAdMediumDto>
      loading={loading}
      label={label || t('common:AD_MEDIUM')}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={{
        filter: searchFilter,
        setFilter: setSearchFilter,
        total,
      }}
      options={options}
      formatOptionLabel={markedOptionLabel}
      dataTestId='ad-medium-dropdown'
    />
  );
});
